import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useCallback, useEffect } from 'react';
import PaymentCheckTab from './PaymentCheckTab';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Upload, Download, FileText, AlertCircle, CheckCircle2, Loader2, ArrowRight, Building2, CreditCard, ChevronDown, ChevronRight, Archive, Clock, Calendar, Users, Search, Settings } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import InvoicePaymentTab from './InvoicePaymentTab';
import BookingInvoicesTab from './BookingInvoicesTab';
import SepaSplitPanel from './sepa/SepaSplitPanel';
import SepaLimitsModal from './sepa/SepaLimitsModal';
import AnomalyResolutionModal from './payment-contacts/AnomalyResolutionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CreditorRow({ creditor: c }) {
  const [open, setOpen] = useState(false);
  const hasDetails = c.transactions && c.transactions.length > 0;
  const fmtEur = (v) => Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  // Detect group label (cost/rsrv) from pmt_inf_id
  const getGroupLabel = (group) => {
    if (!group) return '';
    const g = group.toLowerCase();
    if (g.includes('cost')) return 'COST';
    if (g.includes('rsrv')) return 'RSRV';
    return group.length > 20 ? group.slice(-10) : group;
  };

  return (
    <>
      <tr
        data-testid={`creditor-row-${c.name?.replace(/\s+/g, '-')}`}
        className={`cursor-pointer transition-colors ${open ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
        onClick={() => hasDetails && setOpen(!open)}
      >
        <td className="p-2 text-slate-400">
          {hasDetails && (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
        </td>
        <td className="p-2 text-slate-900 font-medium break-words">{c.name}</td>
        <td className="hidden sm:table-cell p-2 text-slate-500 font-mono text-xs break-all">{c.iban}</td>
        <td className="hidden sm:table-cell p-2 text-right text-slate-600">{c.count}</td>
        <td className="hidden sm:table-cell p-2 text-right font-semibold text-slate-900">{fmtEur(c.total)}</td>
      </tr>
      {open && hasDetails && (
        <tr>
          <td colSpan={5} className="p-0">
            <div className="bg-slate-50 border-t border-b border-slate-200">
              <table className="w-full table-fixed text-xs" data-testid={`creditor-detail-${c.name?.replace(/\s+/g, '-')}`}>
                <thead>
                  <tr className="text-slate-500">
                    <th className="hidden sm:table-cell pl-10 pr-2 py-1.5 text-left font-medium">Riferimento</th>
                    <th className="px-2 py-1.5 text-left font-medium">Causale</th>
                    <th className="hidden sm:table-cell px-2 py-1.5 text-center font-medium">Gruppo</th>
                    <th className="px-2 py-1.5 text-right font-medium pr-3">Importo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {c.transactions.map((tx, j) => (
                    <tr key={j} className="hover:bg-slate-100/50">
                      <td className="hidden sm:table-cell pl-10 pr-2 py-1.5 text-slate-700 font-mono break-all">{tx.reference || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-normal break-words" title={tx.remittance}>{tx.remittance || tx.reference || '-'}</td>
                      <td className="hidden sm:table-cell px-2 py-1.5 text-center">
                        {getGroupLabel(tx.group) && (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide ${
                            getGroupLabel(tx.group) === 'COST' ? 'bg-amber-100 text-amber-700' : 
                            getGroupLabel(tx.group) === 'RSRV' ? 'bg-violet-100 text-violet-700' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {getGroupLabel(tx.group)}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-medium text-slate-800 pr-3">{fmtEur(tx.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function XmlConverterPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [stayFrom, setStayFrom] = useState('');
  const [stayTo, setStayTo] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [splitConfig, setSplitConfig] = useState({ effective_limit_eur: 19500, daily_limit_eur: 20000 });
  const [dailyUsage, setDailyUsage] = useState(null);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [anomalyState, setAnomalyState] = useState(null); // { anomalies, transactions, context, pendingAction }

  const token = localStorage.getItem('hw_token');
  const { user } = useAuth();
  const currentRole = user?.role || '';

  const fetchSplitConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/sepa-split/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setSplitConfig(await r.json());
    } catch (_) { /* ignore */ }
    try {
      const r2 = await fetch(`${API_URL}/api/admin/sepa-split/daily-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r2.ok) setDailyUsage(await r2.json());
    } catch (_) { /* ignore */ }
  }, [token]);

  // Carica config splitter una volta
  useEffect(() => {
    fetchSplitConfig();
  }, [fetchSplitConfig]);

  const handleFile = useCallback(async (f) => {
    if (!f || !f.name.toLowerCase().endsWith('.xml')) {
      setError('Seleziona un file XML valido');
      return;
    }
    setFile(f);
    setError('');
    setSuccess('');
    setPreview(null);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await fetch(`${API_URL}/api/admin/convert-xml-bonifici/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      const data = await res.json();
      setPreview(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleConvert = async () => {
    if (!file) return;
    // Step 1: pre-flight anomaly check su creditors_summary (preview Sabadell)
    if (preview?.creditors_summary?.length) {
      const transactions = (preview.creditors_summary || []).flatMap(c =>
        (c.transactions && c.transactions.length > 0)
          ? c.transactions.map((t, idx) => ({ ref: `${c.iban}_${idx}`, name: c.name, iban: c.iban, amount: t.amount }))
          : [{ ref: `${c.iban}_agg`, name: c.name, iban: c.iban, amount: c.total }]
      );
      try {
        const r = await fetch(`${API_URL}/api/payment-contacts/detect-anomalies`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactions, context: 'xml_generation' }),
        });
        if (r.ok) {
          const data = await r.json();
          if ((data.auto_resolved || []).length > 0) {
            toast.info(`${data.auto_resolved.length} conflitto/i già noto/i applicato/i automaticamente`);
          }
          if ((data.anomalies || []).length > 0) {
            // Pausa conversione: apri modale risoluzione anomalie
            setAnomalyState({ anomalies: data.anomalies, transactions, context: 'xml_generation', pendingAction: 'sabadell_convert' });
            return; // l'utente deve risolvere prima di proseguire
          }
        }
      } catch (e) {
        // Non bloccare la conversione se il check fallisce — solo warning
        console.warn('Anomaly detection skipped:', e.message);
      }
    }
    await doConvert();
  };

  const doConvert = async () => {
    setConverting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (stayFrom) formData.append('stay_date_from', stayFrom);
      if (stayTo) formData.append('stay_date_to', stayTo);
      // Dopo una risoluzione anomalie, usa la rubrica come fonte canonica per name/IBAN
      formData.append('apply_rubrica_overrides', 'true');
      const res = await fetch(`${API_URL}/api/admin/convert-xml-bonifici`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+?)"/);
      const filename = match ? match[1] : 'bonifici_banca_asti.xml';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(`File convertito e scaricato: ${filename}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setConverting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError('');
    setSuccess('');
  };

  const [activeTab, setActiveTab] = useState('sabadell');

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t) setActiveTab(t);
  }, []);
  
  // Booking.com state
  const [bkFile, setBkFile] = useState(null);
  const [bkPreview, setBkPreview] = useState(null);
  const [bkLoading, setBkLoading] = useState(false);
  const [bkConverting, setBkConverting] = useState(false);
  const [bkError, setBkError] = useState('');
  const [bkSuccess, setBkSuccess] = useState('');
  const [bkStayFrom, setBkStayFrom] = useState('');
  const [bkStayTo, setBkStayTo] = useState('');

  const handleBkFile = useCallback(async (f) => {
    if (!f || (!f.name.toLowerCase().endsWith('.xlsx') && !f.name.toLowerCase().endsWith('.xls'))) {
      setBkError('Seleziona un file Excel (.xlsx) valido');
      return;
    }
    setBkFile(f);
    setBkError('');
    setBkSuccess('');
    setBkPreview(null);
    setBkLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await fetch(`${API_URL}/api/admin/convert-booking-commissions/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setBkPreview(await res.json());
    } catch (e) {
      setBkError(e.message);
    } finally {
      setBkLoading(false);
    }
  }, [token]);

  const handleBkConvert = async () => {
    if (!bkFile) return;
    // Pre-flight check anomalie su transazioni booking
    if (bkPreview?.transactions?.length) {
      const transactions = bkPreview.transactions.map((t, idx) => ({
        ref: `bk_${idx}`,
        name: bkPreview.creditor || t.name || '',
        iban: bkPreview.creditor_iban || t.iban || '',
        amount: t.amount,
      }));
      try {
        const r = await fetch(`${API_URL}/api/payment-contacts/detect-anomalies`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactions, context: 'xml_generation' }),
        });
        if (r.ok) {
          const data = await r.json();
          if ((data.auto_resolved || []).length > 0) {
            toast.info(`${data.auto_resolved.length} conflitto/i già noto/i applicato/i automaticamente`);
          }
          if ((data.anomalies || []).length > 0) {
            setAnomalyState({ anomalies: data.anomalies, transactions, context: 'xml_generation', pendingAction: 'booking_convert' });
            return;
          }
        }
      } catch (e) {
        console.warn('Anomaly detection skipped:', e.message);
      }
    }
    await doBkConvert();
  };

  const doBkConvert = async () => {
    setBkConverting(true);
    setBkError('');
    try {
      const formData = new FormData();
      formData.append('file', bkFile);
      if (bkStayFrom) formData.append('stay_date_from', bkStayFrom);
      if (bkStayTo) formData.append('stay_date_to', bkStayTo);
      formData.append('apply_rubrica_overrides', 'true');
      const res = await fetch(`${API_URL}/api/admin/convert-booking-commissions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 'commissioni_booking.xml';
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setBkSuccess(`File convertito e scaricato: ${filename}`);
    } catch (e) {
      setBkError(e.message);
    } finally {
      setBkConverting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="xml-converter-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">Convertitore XML Bonifici</h1>
          <p className="text-sm text-slate-500 mt-1">
            Genera file CBI per Banca di Asti — Istantanei:{' '}
            <span className="font-semibold text-slate-700">{Number(splitConfig.instant_effective_eur || splitConfig.effective_limit_eur || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</span>
            {' '}/giorno — Ordinari:{' '}
            <span className="font-semibold text-slate-700">{Number(splitConfig.ordinary_effective_eur || 49500).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</span>
            {' '}/giorno
          </p>
          {dailyUsage && (() => {
            const u = dailyUsage.usage?.[dailyUsage.date_from];
            const iRem = u ? u.instant_remaining : dailyUsage.instant_effective_eur;
            const oRem = u ? u.ordinary_remaining : dailyUsage.ordinary_effective_eur;
            const fmt = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
            return (
              <p className="text-xs mt-1" data-testid="daily-remaining-header">
                <span className={`inline-flex max-w-full flex-wrap items-center gap-1 px-2 py-0.5 rounded-full ${u ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
                  Residuo oggi — istantanei: <strong>{fmt(iRem)}</strong> · ordinari: <strong>{fmt(oRem)}</strong>
                  {u && <> (già generati {fmt((u.instant_used || 0) + (u.ordinary_used || 0) + (u.unknown_used || 0))})</>}
                </span>
              </p>
            );
          })()}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowLimitsModal(true)}
          data-testid="open-sepa-limits-btn"
          className="self-start"
        >
          <Settings className="h-4 w-4 mr-2" />
          Configura limiti
        </Button>
      </div>

      <SepaLimitsModal
        open={showLimitsModal}
        onClose={() => setShowLimitsModal(false)}
        onSaved={() => fetchSplitConfig()}
        currentRole={currentRole}
      />

      {/* Tabs */}
      <div className="grid w-full grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 sm:flex sm:w-fit">
        <button
          onClick={() => setActiveTab('sabadell')}
          className={`px-2 py-2 rounded-md text-xs sm:px-4 sm:text-sm font-medium transition-colors ${
            activeTab === 'sabadell' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-sabadell"
        >
          Sabadell &rarr; Banca di Asti
        </button>
        <button
          onClick={() => setActiveTab('booking')}
          className={`px-2 py-2 rounded-md text-xs sm:px-4 sm:text-sm font-medium transition-colors ${
            activeTab === 'booking' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-booking"
        >
          Commissioni Booking.com
        </button>
        <button
          onClick={() => setActiveTab('bkinvoices')}
          className={`px-2 py-2 rounded-md text-xs sm:px-4 sm:text-sm font-medium transition-colors ${
            activeTab === 'bkinvoices' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-bkinvoices"
        >
          Fatture Booking.com
        </button>
        <button
          onClick={() => setActiveTab('fatture')}
          className={`px-2 py-2 rounded-md text-xs sm:px-4 sm:text-sm font-medium transition-colors ${
            activeTab === 'fatture' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-fatture"
        >
          Pagamento Fatture
        </button>
        <button
          onClick={() => setActiveTab('controllo')}
          className={`col-span-2 px-2 py-2 rounded-md text-xs sm:col-auto sm:px-4 sm:text-sm font-medium transition-colors ${
            activeTab === 'controllo' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-controllo"
        >
          Controllo Pagamenti
        </button>
      </div>

      {activeTab === 'controllo' && <PaymentCheckTab />}

      {activeTab === 'bkinvoices' && <BookingInvoicesTab />}

      {activeTab === 'sabadell' && (<div className="space-y-6">

      {/* Conversion Flow */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="p-4 text-center">
            <Building2 className="h-8 w-8 mx-auto mb-2 text-red-600" />
            <p className="text-xs font-semibold text-red-800">ORIGINALE</p>
            <p className="text-sm font-medium mt-1">Holiday World Tenerife SL</p>
            <p className="text-xs text-slate-500">Banco Sabadell (ES)</p>
            <p className="text-xs text-slate-400 mt-1">pain.001.001.03</p>
          </CardContent>
        </Card>

        <div className="flex items-center justify-center">
          <ArrowRight className="h-8 w-8 text-slate-400" />
        </div>

        <Card className="border-green-200 bg-green-50/30">
          <CardContent className="p-4 text-center">
            <CreditCard className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-xs font-semibold text-green-800">CONVERTITO</p>
            <p className="text-sm font-medium mt-1">Agora Business Lab srl</p>
            <p className="text-xs text-slate-500">Banca di Asti (IT)</p>
            <p className="text-xs text-slate-400 mt-1">CBI 00.04.01</p>
          </CardContent>
        </Card>
      </div>

      {/* Upload Area */}
      {!file && (
        <Card>
          <CardContent className="p-0">
            <div
              data-testid="xml-upload-dropzone"
              className={`p-12 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onClick={() => document.getElementById('xml-file-input').click()}
            >
              <Upload className="h-12 w-12 mx-auto mb-4 text-slate-400" />
              <p className="text-lg font-medium text-slate-700">
                Trascina qui il file XML Sabadell
              </p>
              <p className="text-sm text-slate-500 mt-2">
                oppure clicca per selezionare il file
              </p>
              <input
                id="xml-file-input"
                data-testid="xml-file-input"
                type="file"
                accept=".xml"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-slate-600">Analisi del file in corso...</p>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="xml-error">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Errore</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="xml-success">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Anteprima conversione
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Transazioni</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="preview-tx-count">
                    {preview.total_transactions}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Totale</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="preview-total-amount">
                    {Number(preview.total_amount).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Data esecuzione</p>
                  <p className="text-lg font-semibold text-slate-900" data-testid="preview-execution-date">
                    {preview.execution_date || '-'}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Causale XML</p>
                  <p className="text-sm font-semibold text-blue-700" data-testid="preview-payment-description">
                    {preview.causale_suffix}
                  </p>
                </div>
              </div>

              {(preview.already_paid_duplicates || []).length > 0 && (
                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4" data-testid="converter-already-paid-card">
                  <div className="flex items-start gap-2 text-red-900 text-sm">
                    <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                    <div className="space-y-1.5">
                      <strong data-testid="converter-already-paid-title">
                        🚨 {preview.already_paid_duplicates.length === 1 ? '1 bonifico GIÀ PRESENTE' : `${preview.already_paid_duplicates.length} bonifici GIÀ PRESENTI`} in lotti precedenti — rischio DOPPIO PAGAMENTO
                      </strong>
                      <p className="text-xs">
                        Questi riferimenti erano già inclusi in file generati negli ultimi 60 giorni.
                        Se scarichi e carichi in banca questo file verranno pagati DI NUOVO.
                      </p>
                      {preview.already_paid_duplicates.map((d, i) => (
                        <div key={i} className="font-mono text-xs" data-testid={`converter-already-paid-dup-${i}`}>
                          • {d.reference} — {Number(d.amount).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })} — {d.beneficiary} → già in{' '}
                          <strong>{d.previous_batch?.file || 'lotto precedente'}</strong>
                          {d.previous_batch?.created_at ? ` del ${new Date(d.previous_batch.created_at).toLocaleDateString('it-IT')}` : ''}
                          {' '}({d.previous_batch?.status === 'executed' ? 'GIÀ ESEGUITO in banca' : 'generato, non ancora eseguito'})
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Creditors Summary */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Beneficiari ({preview.creditors_summary?.length || 0})
                  <span className="text-xs text-slate-400 ml-2 font-normal">Clicca su un beneficiario per vedere le transazioni</span>
                </p>
                <div className="max-h-[500px] overflow-y-auto border rounded-lg">
                  <table className="w-full table-fixed text-sm sm:table-auto">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-2 font-medium text-slate-600 w-8"></th>
                        <th className="text-left p-2 font-medium text-slate-600">Beneficiario</th>
                        <th className="hidden sm:table-cell text-left p-2 font-medium text-slate-600">IBAN</th>
                        <th className="hidden sm:table-cell text-right p-2 font-medium text-slate-600">Bonifici</th>
                        <th className="hidden sm:table-cell text-right p-2 font-medium text-slate-600">Totale</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(preview.creditors_summary || []).map((c, i) => (
                        <CreditorRow key={i} creditor={c} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stay Dates + Action Buttons */}
          <div className="space-y-3">
            <div className="flex flex-col gap-3 bg-slate-50 p-3 rounded-lg sm:flex-row sm:items-center">
              <span className="text-sm font-medium text-slate-700">Periodo soggiorno:</span>
              <input type="date" value={stayFrom} onChange={(e) => setStayFrom(e.target.value)} className="px-2 py-1.5 border rounded text-sm" />
              <span className="text-sm text-slate-500">al</span>
              <input type="date" value={stayTo} onChange={(e) => setStayTo(e.target.value)} className="px-2 py-1.5 border rounded text-sm" />
            </div>

            {/* SEPA Split Panel - shown when total exceeds threshold OR duplicates found */}
            {(Number(preview.total_amount) > splitConfig.effective_limit_eur ||
              (preview.already_paid_duplicates || []).length > 0) && (
              <SepaSplitPanel
                sourceFile={file}
                planEndpoint="/api/admin/sepa-split/sabadell-plan"
                downloadEndpoint="/api/admin/sepa-split/sabadell-download"
                extraFormFields={{ stay_date_from: stayFrom, stay_date_to: stayTo }}
                filenamePrefix="bonifici_banca_asti"
                title="Split SEPA — Limite Banca di Asti (Sabadell)"
                onDownloaded={({ split_group_id }) => {
                  setSuccess(`ZIP scaricato. Gruppo split: ${split_group_id}`);
                  fetchSplitConfig();
                }}
              />
            )}

            <div className="flex flex-wrap gap-3">
            <Button
              data-testid="convert-download-btn"
              onClick={handleConvert}
              disabled={converting || Number(preview.total_amount) > splitConfig.effective_limit_eur}
              className="bg-green-600 hover:bg-green-700"
              title={Number(preview.total_amount) > splitConfig.effective_limit_eur ? 'Disabilitato: totale supera la soglia, usa lo split sopra' : ''}
            >
              {converting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Conversione...</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> Converti e Scarica XML</>
              )}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="reset-btn">
              Carica altro file
            </Button>
            </div>
          </div>
        </div>
      )}
      </div>)}

      {/* === TAB: Booking.com Commissions === */}
      {activeTab === 'booking' && (
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-slate-900">Commissioni Booking.com</h3>
                  <p className="text-xs text-slate-500">Carica il file Excel delle commissioni mensili e genera l'XML per il bonifico</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
                <p className="font-medium text-blue-800">Destinatario fisso:</p>
                <p className="text-blue-700">BOOKING.COM B.V. - IBAN: IT95Z0310401600000000580214 - Milano</p>
              </div>

              {!bkFile ? (
                <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <Upload className="h-10 w-10 text-slate-400 mb-2" />
                  <p className="text-sm font-medium text-slate-700">Carica file Excel commissioni</p>
                  <p className="text-xs text-slate-400 mt-1">.xlsx con colonne: Invoice, ID, Property Name, Amount</p>
                  <input type="file" className="hidden" accept=".xlsx,.xls" onChange={(e) => e.target.files[0] && handleBkFile(e.target.files[0])} data-testid="bk-file-input" />
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">{bkFile.name}</span>
                  <button onClick={() => { setBkFile(null); setBkPreview(null); setBkError(''); setBkSuccess(''); }} className="ml-auto text-slate-400 hover:text-red-500 text-sm">Cambia file</button>
                </div>
              )}

              {bkLoading && <div className="flex items-center gap-2 text-slate-500 mt-3"><Loader2 className="h-4 w-4 animate-spin" /> Analisi file...</div>}
              {bkError && <div className="flex items-center gap-2 text-red-600 mt-3"><AlertCircle className="h-4 w-4" /> {bkError}</div>}
              {bkSuccess && <div className="flex items-center gap-2 text-green-600 mt-3"><CheckCircle2 className="h-4 w-4" /> {bkSuccess}</div>}
            </CardContent>
          </Card>

          {bkPreview && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{bkPreview.total_transactions}</p>
                      <p className="text-xs text-blue-600">Commissioni</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{Number(bkPreview.total_amount).toLocaleString('it-IT', {style:'currency',currency:'EUR'})}</p>
                      <p className="text-xs text-green-600">Totale</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-bold text-slate-700">{bkPreview.creditor}</p>
                      <p className="text-xs text-slate-500 font-mono">{bkPreview.creditor_iban}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {bkPreview.excluded_deferred?.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="bk-excluded-deferred">
                  <b>⏸ {bkPreview.excluded_deferred.length} fatture ESCLUSE perché rinviate</b> (case chiuse a lungo — vedi tab "Fatture Booking.com"):
                  <span className="block text-xs mt-1">{bkPreview.excluded_deferred.map(t => `${t.invoice} (${t.property_name}, ${Number(t.amount).toLocaleString('it-IT', {style:'currency',currency:'EUR'})})`).join(' · ')}</span>
                </div>
              )}

              {bkPreview.excluded_vat?.length > 0 && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800" data-testid="bk-excluded-vat">
                  <b>🚫 {bkPreview.excluded_vat.length} fatture ESCLUSE perché con IVA addebitata</b> (da contestare a Booking — gestiscile dal Registro IVA nel tab "Fatture Booking.com"):
                  <span className="block text-xs mt-1">{bkPreview.excluded_vat.map(t => `${t.invoice} (${t.property_name}, ${Number(t.amount).toLocaleString('it-IT', {style:'currency',currency:'EUR'})})`).join(' · ')}</span>
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Dettaglio commissioni ({bkPreview.transactions?.length})</p>
                  <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium text-slate-600">Fattura</th>
                          <th className="text-left p-2 font-medium text-slate-600">Immobile</th>
                          <th className="text-left p-2 font-medium text-slate-600">Causale</th>
                          <th className="text-right p-2 font-medium text-slate-600">Importo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(bkPreview.transactions || []).map((t, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2 font-mono text-xs text-slate-600">{t.invoice}</td>
                            <td className="p-2 text-slate-800 text-xs">{t.property_name}</td>
                            <td className="p-2 text-slate-500 text-xs truncate max-w-[250px]" title={t.causale}>{t.causale}</td>
                            <td className="p-2 text-right font-medium">{Number(t.amount).toLocaleString('it-IT', {style:'currency',currency:'EUR'})}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                  <span className="text-sm font-medium text-slate-700">Fattura di:</span>
                  <input type="month" value={bkStayFrom} onChange={(e) => setBkStayFrom(e.target.value)} className="px-2 py-1.5 border rounded text-sm" />
                  <span className="text-sm font-medium text-slate-700">Mese riferimento:</span>
                  <input type="month" value={bkStayTo} onChange={(e) => setBkStayTo(e.target.value)} className="px-2 py-1.5 border rounded text-sm" />
                </div>

                {/* SEPA Split Panel - shown only when total exceeds threshold */}
                {Number(bkPreview.total_amount) > splitConfig.effective_limit_eur && (
                  <SepaSplitPanel
                    sourceFile={bkFile}
                    planEndpoint="/api/admin/sepa-split/booking-plan"
                    downloadEndpoint="/api/admin/sepa-split/booking-download"
                    extraFormFields={{ stay_date_from: bkStayFrom, stay_date_to: bkStayTo }}
                    filenamePrefix="commissioni_booking"
                    title="Split SEPA — Limite Banca di Asti (Booking.com)"
                    onDownloaded={({ split_group_id }) => {
                      setBkSuccess(`ZIP scaricato. Gruppo split: ${split_group_id}`);
                      fetchSplitConfig();
                    }}
                  />
                )}

                <div className="flex gap-3">
                  <Button onClick={handleBkConvert} disabled={bkConverting || Number(bkPreview.total_amount) > splitConfig.effective_limit_eur} className="bg-green-600 hover:bg-green-700" data-testid="bk-convert-btn"
                    title={Number(bkPreview.total_amount) > splitConfig.effective_limit_eur ? 'Disabilitato: totale supera la soglia, usa lo split sopra' : ''}>
                    {bkConverting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Conversione...</> : <><Download className="h-4 w-4 mr-2" /> Genera XML Bonifico</>}
                  </Button>
                  <Button variant="outline" onClick={() => { setBkFile(null); setBkPreview(null); setBkError(''); setBkSuccess(''); }}>Carica altro file</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}


      {activeTab === 'fatture' && (
        <InvoicePaymentTab />
      )}

      {anomalyState && (
        <AnomalyResolutionModal
          anomalies={anomalyState.anomalies}
          transactions={anomalyState.transactions}
          context={anomalyState.context}
          onClose={() => setAnomalyState(null)}
          onResolved={async () => {
            const pending = anomalyState.pendingAction;
            setAnomalyState(null);
            // Rubrica aggiornata: rilancia la conversione
            if (pending === 'sabadell_convert') {
              await doConvert();
            } else if (pending === 'booking_convert') {
              await doBkConvert();
            }
          }}
        />
      )}

      {/* === TAB: Archivio nella pagina standalone /payment-archive === */}
    </div>
  );
}
